// Modal "Segnala un problema" - richiede login
import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useAuthStore } from '@/store/authStore';
import { useI18n } from '@/i18n';
import { toast } from '@/hooks/useToast';
import { isSupabaseConfigured, getSupabase } from '@/lib/supabase';
import type { ReportType } from '@/types';
import { Bug, Lightbulb, MessageSquare, LogIn, AlertCircle } from 'lucide-react';

export function ReportProblemModal({
  open,
  onClose,
  onRequireLogin,
}: {
  open: boolean;
  onClose: () => void;
  onRequireLogin: () => void;
}) {
  const { user } = useAuthStore();
  const { t } = useI18n();
  const [type, setType] = useState<ReportType>('bug');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (!user) {
      onRequireLogin();
      return;
    }
    if (!isSupabaseConfigured()) {
      toast.error(t('settings.report_error'));
      return;
    }
    if (!subject.trim() || !message.trim()) {
      toast.error(t('settings.report_error'));
      return;
    }
    setSubmitting(true);
    try {
      const client = getSupabase();
      const { error } = await client.from('reports').insert({
        user_id: user.id,
        user_email: user.email,
        type,
        subject: subject.trim(),
        message: message.trim(),
        app_version: '1.0.0',
        device_info: navigator.userAgent,
        created_at: new Date().toISOString(),
      });
      if (error) throw error;
      toast.success(t('settings.report_sent'));
      setSubject('');
      setMessage('');
      setType('bug');
      onClose();
    } catch (e: any) {
      console.error('[report] error', e);
      toast.error(t('settings.report_error'));
    } finally {
      setSubmitting(false);
    }
  }

  const reportTypes: { value: ReportType; label: string; icon: any }[] = [
    { value: 'bug', label: t('settings.report_type_bug'), icon: Bug },
    { value: 'suggestion', label: t('settings.report_type_suggestion'), icon: Lightbulb },
    { value: 'other', label: t('settings.report_type_other'), icon: MessageSquare },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('settings.report_title')}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            loading={submitting}
            onClick={handleSubmit}
            disabled={!subject.trim() || !message.trim()}
          >
            {t('settings.report_submit')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">{t('settings.report_desc')}</p>

        {!user && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 p-3 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <p>{t('settings.report_require_login')}</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={onRequireLogin}
              >
                <LogIn size={14} /> {t('settings.login_register')}
              </Button>
            </div>
          </div>
        )}

        <Field label={t('settings.report_type')}>
          <div className="grid grid-cols-3 gap-2">
            {reportTypes.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setType(value)}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all text-xs font-medium ${
                  type === value
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                    : 'border-slate-200 dark:border-slate-700 text-slate-500'
                }`}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>
        </Field>

        <Field label={t('settings.report_subject')}>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t('settings.report_subject_ph')}
            maxLength={120}
          />
        </Field>

        <Field label={t('settings.report_message')}>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('settings.report_message_ph')}
            rows={5}
            maxLength={2000}
          />
        </Field>
      </div>
    </Modal>
  );
}
